/**
 * Puck Schema Generator
 * Converts ElementSchema definitions to Puck field configurations
 */

import * as React from 'react'

import { sectionSchemaRegistry } from '@/lib/section-schema-registry'

import {
  BooleanFieldRender,
  ColorFieldRender,
  ImageFieldRender,
  VideoFieldRender,
} from '@/components/puck/puck-custom-fields'

import type { ElementSchema, SectionElementSchema } from '@/types/element-schema.type'

/**
 * Puck field type (simplified for compatibility)
 */
type PuckFieldType = 'text' | 'textarea' | 'number' | 'select' | 'radio' | 'array' | 'external' | 'custom' | 'json'

/**
 * Puck field configuration
 */
export interface PuckField {
  type: PuckFieldType
  label: string
  options?: Array<{ label: string; value: string }>
  /** For array field types: defines the fields for each array item */
  arrayFields?: Record<string, PuckField>
  /** For array field types: default values for new array items */
  defaultItemProps?: Record<string, unknown>
  /** For custom field types */
  render?: (props: {
    value: unknown
    onChange: (value: unknown) => void
    id?: string
    name?: string
  }) => React.ReactNode
}

/**
 * Map element type to Puck field type
 */
function getFieldType(type: ElementSchema['type']): PuckFieldType {
  const typeMap: Record<string, PuckFieldType> = {
    text: 'text',
    textarea: 'textarea',
    number: 'number',
    boolean: 'custom', // Custom toggle/checkbox
    date: 'text',
    color: 'custom', // Custom color picker
    image: 'custom', // Custom image URL + preview
    video: 'custom', // Custom video URL input
    file: 'text',
    select: 'select',
    radio: 'radio',
    checkbox: 'text', // Use text for checkbox
    json: 'textarea', // Use textarea for JSON
    array: 'array',
    external: 'external',
    // New types - use custom for complex types
    layout: 'custom',
    background: 'custom',
    slides: 'custom',
    container: 'custom',
  }
  return typeMap[type] || 'text'
}

/**
 * Convert ElementSchema to Puck field configuration
 */
export function schemaToPuckField(schema: ElementSchema): PuckField {
  const field: PuckField = {
    type: getFieldType(schema.type),
    label: schema.label,
  }

  // Add options for select/radio
  if ((schema.type === 'select' || schema.type === 'radio') && schema.options) {
    field.options = schema.options.map(opt => ({
      label: opt.label,
      value: String(opt.value),
    }))
  }

  // Add arrayFields for array types with children
  if (schema.type === 'array' && schema.children && schema.children.length > 0) {
    const arrayFields: Record<string, PuckField> = {}
    const defaultItemProps: Record<string, unknown> = {}
    schema.children.forEach(child => {
      arrayFields[child.key] = schemaToPuckField(child)
      if (child.default !== undefined) {
        defaultItemProps[child.key] = child.default
      } else {
        defaultItemProps[child.key] = child.type === 'number' ? 0 : ''
      }
    })
    field.arrayFields = arrayFields
    field.defaultItemProps = defaultItemProps
  }

  // Custom render functions for specific types
  switch (schema.type) {
    case 'color':
      field.render = ColorFieldRender
      break
    case 'image':
      field.render = ImageFieldRender
      break
    case 'video':
      field.render = VideoFieldRender
      break
    case 'boolean':
      field.render = BooleanFieldRender
      break
    // Complex types stored as JSON object
    case 'layout':
    case 'background':
    case 'slides':
    case 'container':
      field.type = 'json'
      break
  }

  return field
}

/**
 * Generate Puck fields from a section schema
 */
export function generatePuckFields(componentType: string): Record<string, PuckField> {
  const schema = sectionSchemaRegistry.get(componentType)
  if (!schema) {
    // eslint-disable-next-line no-console
    console.warn(`No schema found for ${componentType}`)
    return {}
  }

  const fields: Record<string, PuckField> = {}

  schema.elements.forEach(element => {
    fields[element.key] = schemaToPuckField(element)
  })

  return fields
}

/**
 * Generate default props from a section schema
 */
export function generateDefaultProps(componentType: string): Record<string, unknown> {
  const schema = sectionSchemaRegistry.get(componentType)
  if (!schema) return {}

  const defaultProps: Record<string, unknown> = {}

  schema.elements.forEach(element => {
    if (element.default !== undefined) {
      defaultProps[element.key] = element.default
    } else {
      // Generate default based on type
      switch (element.type) {
        case 'text':
        case 'textarea':
          defaultProps[element.key] = ''
          break
        case 'number':
          defaultProps[element.key] = 0
          break
        case 'boolean':
        case 'checkbox':
          defaultProps[element.key] = false
          break
        case 'array':
        case 'slides':
          defaultProps[element.key] = []
          break
        case 'json':
        case 'layout':
        case 'background':
        case 'container':
          defaultProps[element.key] = {}
          break
        default:
          defaultProps[element.key] = null
      }
    }
  })

  return defaultProps
}

/**
 * Check if a component has a registered schema
 */
export function hasComponentSchema(componentType: string): boolean {
  return sectionSchemaRegistry.has(componentType)
}

/**
 * Get schema label for a component
 */
export function getComponentLabel(componentType: string): string | undefined {
  const schema = sectionSchemaRegistry.get(componentType)
  return schema?.label
}

/**
 * Get schema for a component
 */
export function getComponentSchema(componentType: string): SectionElementSchema | undefined {
  return sectionSchemaRegistry.get(componentType)
}

// ============================================
// BLOCK-BASED SCHEMA GENERATION
// ============================================

/**
 * Generate Puck fields from element blocks
 */
export function generatePuckFieldsFromBlocks(blocks: ElementSchema[]): Record<string, PuckField> {
  const fields: Record<string, PuckField> = {}

  blocks.forEach(element => {
    fields[element.key] = schemaToPuckField(element)
  })

  return fields
}

/**
 * Generate default props from element blocks
 */
export function generateDefaultPropsFromBlocks(blocks: ElementSchema[]): Record<string, unknown> {
  const defaultProps: Record<string, unknown> = {}

  blocks.forEach(element => {
    if (element.default !== undefined) {
      defaultProps[element.key] = element.default
    } else {
      switch (element.type) {
        case 'text':
        case 'textarea':
          defaultProps[element.key] = ''
          break
        case 'number':
          defaultProps[element.key] = 0
          break
        case 'boolean':
        case 'checkbox':
          defaultProps[element.key] = false
          break
        case 'array':
        case 'slides':
          defaultProps[element.key] = []
          break
        case 'json':
        case 'layout':
        case 'background':
        case 'container':
          defaultProps[element.key] = {}
          break
        default:
          defaultProps[element.key] = null
      }
    }
  })

  return defaultProps
}

// ============================================
// SPECIALIZED DEFAULT GENERATORS
// ============================================

/**
 * Get default layout value
 */
export function getDefaultLayoutValue(): Record<string, unknown> {
  return {
    columns: 1,
    gap: 'gap-8',
    align: 'center',
    direction: 'row',
    wrap: true,
  }
}

/**
 * Get default background value
 */
export function getDefaultBackgroundValue(): Record<string, unknown> {
  return {
    type: 'none',
  }
}

/**
 * Get default slides value
 */
export function getDefaultSlidesValue(): Record<string, unknown> {
  return {
    items: [],
    autoplay: false,
    autoplayInterval: 5000,
    showArrows: true,
    showDots: true,
    infinite: true,
  }
}

/**
 * Get default container value
 */
export function getDefaultContainerValue(): Record<string, unknown> {
  return {
    children: [],
    maxWidth: 'max-w-7xl',
    padding: 'py-16 px-4',
    margin: '',
  }
}
